import React from 'react';
import {
  Box, Typography, Chip, Paper, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { AuditReport } from '../../services/customsAuditService';

interface AuditDashboardProps {
  report: AuditReport;
  appliedCount: number;
  fixableCount: number;
  onClose: () => void;
  onDownloadPdf: () => void;
  onAreaClick: (area: string) => void;
}

const STATUS_CONFIG = {
  compliant: { label: 'Compliant', color: 'success' as const },
  requires_review: { label: 'Requires Review', color: 'warning' as const },
  non_compliant: { label: 'Non-Compliant', color: 'error' as const },
};

const AREA_ICONS: Record<string, string> = {
  description: '📝', tariff: '🏷️', price: '💰', origin: '🌍',
  export_control: '🔒', physical: '📦', tampering: '🔍',
};

const AREA_LABELS: Record<string, string> = {
  description: 'Description', tariff: 'Tariff', price: 'Price',
  origin: 'Origin', export_control: 'Export Ctrl', physical: 'Physical',
  tampering: 'Tampering',
};

function getScoreColor(score: number): string {
  if (score >= 80) return '#4caf50';
  if (score >= 60) return '#ff9800';
  if (score >= 40) return '#f44336';
  return '#d32f2f';
}

const AuditDashboard: React.FC<AuditDashboardProps> = ({
  report, appliedCount, fixableCount, onClose, onDownloadPdf, onAreaClick,
}) => {
  const theme = useTheme();
  const totalFindings = report.area_results.reduce((sum, ar) => sum + ar.findings.length, 0);
  const statusCfg = STATUS_CONFIG[report.overall_status] || STATUS_CONFIG.requires_review;

  return (
    <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Customs Audit Results</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Export PDF">
            <IconButton size="small" onClick={onDownloadPdf}><PictureAsPdfIcon /></IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate" value={report.overall_risk_score} size={80}
            thickness={6}
            sx={{ color: getScoreColor(report.overall_risk_score), transform: 'rotate(-90deg) !important' }}
          />
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <Typography variant="h5" fontWeight={800}>{report.overall_risk_score}</Typography>
            <Typography variant="caption" color="text.secondary">/100</Typography>
          </Box>
        </Box>

        <Box>
          <Chip label={statusCfg.label} color={statusCfg.color} size="small" sx={{ mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {totalFindings} findings &middot; {fixableCount} fixable &middot; {appliedCount}/{fixableCount} applied &middot; {(report.processing_time_ms / 1000).toFixed(1)}s
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {report.area_results.map(ar => {
          const statusColor = ar.status === 'pass' ? 'success' : ar.status === 'warning' ? 'warning' : 'error';
          return (
            <Paper
              key={ar.area}
              onClick={() => onAreaClick(ar.area)}
              sx={{
                p: 1, minWidth: 90, cursor: 'pointer', textAlign: 'center',
                border: '1px solid', borderColor: `${statusColor}.light`,
                bgcolor: alpha(theme.palette[statusColor].main, 0.04),
                '&:hover': { bgcolor: alpha(theme.palette[statusColor].main, 0.1) },
                borderRadius: 1.5,
              }}
            >
              <Typography variant="caption" display="block">{AREA_ICONS[ar.area] || '📋'}</Typography>
              <Typography variant="caption" fontWeight={600} display="block" noWrap>
                {AREA_LABELS[ar.area] || ar.area}
              </Typography>
              <Typography variant="h6" fontWeight={700} color={`${statusColor}.main`}>{ar.score}</Typography>
              <Typography variant="caption" color="text.secondary">{ar.findings.length} issues</Typography>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

export default AuditDashboard;
